import { isOffline, saveLane, saveSpot, uploadPhoto, type ApiError } from './api';

const DB_NAME = 'mdrc-outbox';
const DB_VERSION = 1;
const QUEUE = 'queue';
const DRAFTS = 'drafts';

export interface PendingPhoto {
  filename: string;
  contentType: string;
  data: ArrayBuffer;
}

export interface QueueItem {
  id: string;
  kind: 'spot_create' | 'spot_patch' | 'lane_upsert';
  payload: Record<string, unknown>;
  photos: PendingPhoto[];
  createdAt: string;
  status: 'pending' | 'failed';
  lastError?: string;
}

export interface WorkingDraft {
  id: 'working';
  state: unknown;
  photos: PendingPhoto[];
  updatedAt: string;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(QUEUE)) {
        db.createObjectStore(QUEUE, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(DRAFTS)) {
        db.createObjectStore(DRAFTS, { keyPath: 'id' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function notify(): void {
  window.dispatchEvent(new CustomEvent('mdrc-queue'));
}

async function putQueue(item: QueueItem): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(QUEUE, 'readwrite');
    tx.objectStore(QUEUE).put(item);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  notify();
}

export async function listQueue(): Promise<QueueItem[]> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(QUEUE, 'readonly');
    const req = tx.objectStore(QUEUE).getAll();
    req.onsuccess = () => resolve((req.result as QueueItem[]) || []);
    req.onerror = () => reject(req.error);
  });
}

export async function queueCount(): Promise<number> {
  const items = await listQueue();
  return items.length;
}

async function removeQueue(id: string): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(QUEUE, 'readwrite');
    tx.objectStore(QUEUE).delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  notify();
}

export async function enqueue(item: QueueItem): Promise<void> {
  await putQueue(item);
}

export async function dropQueue(id: string): Promise<void> {
  await removeQueue(id);
}

export async function saveWorkingDraft(
  state: unknown,
  photos: PendingPhoto[]
): Promise<void> {
  const db = await openDb();
  const record: WorkingDraft = {
    id: 'working',
    state,
    photos,
    updatedAt: new Date().toISOString(),
  };
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(DRAFTS, 'readwrite');
    tx.objectStore(DRAFTS).put(record);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function loadWorkingDraft(): Promise<WorkingDraft | null> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(DRAFTS, 'readonly');
    const req = tx.objectStore(DRAFTS).get('working');
    req.onsuccess = () => resolve((req.result as WorkingDraft) || null);
    req.onerror = () => reject(req.error);
  });
}

export async function clearWorkingDraft(): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(DRAFTS, 'readwrite');
    tx.objectStore(DRAFTS).delete('working');
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

let flushing = false;

export async function flushQueue(): Promise<{ sent: number; left: number }> {
  if (flushing) {
    const left = await queueCount();
    return { sent: 0, left };
  }
  flushing = true;
  let sent = 0;
  try {
    const items = (await listQueue()).sort((a, b) =>
      a.createdAt.localeCompare(b.createdAt)
    );
    for (const item of items) {
      const outcome = await sendItem(item);
      if (outcome === 'sent') sent += 1;
      else if (outcome === 'stop') break;
    }
  } finally {
    flushing = false;
  }
  const left = await queueCount();
  return { sent, left };
}

async function sendItem(item: QueueItem): Promise<'sent' | 'stop' | 'skip'> {
  const keys = Array.isArray(item.payload.evidence_keys)
    ? [...(item.payload.evidence_keys as string[])]
    : [];
  const photos = [...item.photos];
  while (photos.length) {
    const photo = photos[0];
    try {
      const key = await uploadPhoto(
        new Blob([photo.data], { type: photo.contentType }),
        photo.contentType,
        photo.filename
      );
      keys.push(key);
      photos.shift();
    } catch (err) {
      item.photos = photos;
      item.payload = { ...item.payload, evidence_keys: keys };
      item.lastError = (err as Error).message || 'Upload failed';
      item.status = 'pending';
      await putQueue(item);
      return isOffline(err) || (err as ApiError).status === 401 ? 'stop' : 'stop';
    }
  }
  item.photos = [];
  item.payload = { ...item.payload, evidence_keys: keys };
  try {
    if (item.kind === 'spot_patch') {
      const id = String(item.payload.id || '');
      await saveSpot(item.payload, id);
    } else if (item.kind === 'lane_upsert') {
      await saveLane(item.payload);
    } else {
      await saveSpot(item.payload);
    }
    await removeQueue(item.id);
    return 'sent';
  } catch (err) {
    const status = (err as ApiError).status ?? 0;
    item.lastError = (err as Error).message || 'Send failed';
    item.status = status >= 400 && status < 500 && status !== 401 ? 'failed' : 'pending';
    await putQueue(item);
    if (isOffline(err) || status === 401 || status === 0) return 'stop';
    return 'skip';
  }
}

export function startQueueLoop(): void {
  const tick = () => {
    if (document.hidden) return;
    void flushQueue();
  };
  window.addEventListener('online', tick);
  setInterval(tick, 20_000);
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('message', (event) => {
      const data = event.data as { type?: string } | null;
      if (data?.type === 'FLUSH_OUTBOX') tick();
    });
  }
}
